/**
 * BargeeAI — pure behaviour tree for "The Bargee" Stage 1 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Three-phase canal barge fight:
 *   Phase 1 (HP > 50%): boat-hook lunge, barrel-throw, hook-pull (round-robin)
 *   Phase 2 (HP 20–50%): same attacks + 2 deckhands summoned once on phase entry
 *   Phase 3 (HP < 20%):  cycle-0 slot becomes a cleat-swing arc attack
 *
 * Attack cycle (round-robin, advances only on fire):
 *   0 → hook_lunge (phase 1-2) / swing (phase 3)
 *   1 → barrel_throw (fires at any range)
 *   2 → hook_pull   (close-range drag)
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Boat-hook lunge engages from further than a normal melee. */
export const LUNGE_RANGE = 160;
/** Hook-pull grab must be at close quarters. */
export const PULL_RANGE = 80;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Telegraph frames before the lunge strikes. */
export const LUNGE_WINDUP_FRAMES = 18;
/** Telegraph frames before the pull grabs. */
export const PULL_WINDUP_FRAMES = 14;
/** Telegraph frames before the barrel throw. */
export const BARREL_WINDUP_FRAMES = 22;
/** Frames latching onto the deck cleat before the swing arc begins. */
export const SWING_STARTUP_FRAMES = 15;
/** Frames of the full swing arc (wide hitbox, handled by adapter). */
export const SWING_FRAMES = 40;
/** Retreat frames after a lunge or pull. */
export const BACKSTEP_FRAMES = 28;
/** Frames The Bargee bellows while summoning the deckhand crew. */
export const SUMMON_FRAMES = 35;
/** Speed multiplier during backstep. */
export const BARGEE_BACKSTEP_SPEED = 1.3;
/** Base inter-attack cooldown at aggressionScale = 0. */
const BASE_COOLDOWN = 80;

// ── HP phase thresholds ───────────────────────────────────────────────────────
export const PHASE_2_THRESHOLD = 0.5;
export const PHASE_3_THRESHOLD = 0.2;

// ── Types ─────────────────────────────────────────────────────────────────────
export type BargeePhase = 1 | 2 | 3;

export type BargeeAttack = 'hook_lunge' | 'hook_pull' | 'barrel_throw' | 'swing';

export type BargeeNode =
  | 'scan'
  | 'approach'
  | 'hook_lunge_windup'
  | 'hook_pull_windup'
  | 'barrel_windup'
  | 'swing_startup'
  | 'swinging'
  | 'summoning'
  | 'backstep';

export interface BargeeAIState {
  node: BargeeNode;
  phase: BargeePhase;
  cooldown: number;
  windup: number;
  swingFrames: number;
  backstepFrames: number;
  summonFrames: number;
  /** Round-robin: 0 = lunge/swing, 1 = barrel, 2 = pull. */
  attackCycle: number;
  /**
   * True once the deckhand summon has been triggered; prevents re-trigger
   * if HP fluctuates around the phase-2 boundary.
   */
  deckhandsSummoned: boolean;
}

export interface BargeeAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). Drives phase-transition checks each frame. */
  hpFraction: number;
}

export interface BargeeAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  /** The attack type fired this frame, or null. */
  attackType: BargeeAttack | null;
  /** True on the single frame the adapter should spawn 2 deckhands. */
  wantsSummonDeckhands: boolean;
  nextState: BargeeAIState;
}

export function initialBargeeState(): BargeeAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    swingFrames: 0,
    backstepFrames: 0,
    summonFrames: 0,
    attackCycle: 0,
    deckhandsSummoned: false,
  };
}

/**
 * Advance the Bargee behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns
 * hitboxes / deckhand summons based on the output.
 */
export function tickBargeeAI(input: BargeeAIInput, state: BargeeAIState): BargeeAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialBargeeState());
  }

  // ── Phase resolution (re-derived every frame from live HP) ────────────────
  const phase: BargeePhase =
    input.hpFraction <= PHASE_3_THRESHOLD ? 3 : input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;

  const { deckhandsSummoned } = state;

  // ── Phase 2+ entry: begin summoning animation (once only) ─────────────────
  if (phase >= 2 && !deckhandsSummoned && state.node !== 'summoning') {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      phase,
      deckhandsSummoned: true, // lock immediately to prevent re-entry
      cooldown: 0,
      windup: 0,
      swingFrames: 0,
      backstepFrames: 0,
      summonFrames: SUMMON_FRAMES,
    });
  }

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const swingFrames = Math.max(0, state.swingFrames - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const summonFrames = Math.max(0, state.summonFrames - 1);

  // ── Hitstun: freeze animation, reset active states, keep ticking cooldown ─
  if (input.isHitstunned) {
    // If a hit interrupts the summon wind-up, re-arm the dedup flag: the spawn
    // signal only fires at wind-up completion, so without this the deckhands are
    // silently never summoned (getting hit mid-summon is the common case). The
    // phase>=2 gate then re-fires the summon on a later clean frame — still once.
    const interruptedSummon = state.node === 'summoning';
    return noOp(facing, {
      ...state,
      phase,
      deckhandsSummoned: interruptedSummon ? false : deckhandsSummoned,
      node: 'scan',
      cooldown,
      windup: 0,
      swingFrames: 0,
      backstepFrames: 0,
      summonFrames: 0,
    });
  }

  // ── SUMMONING ─────────────────────────────────────────────────────────────
  if (state.node === 'summoning') {
    if (summonFrames > 0) {
      return noOp(facing, { ...state, phase, deckhandsSummoned, cooldown, summonFrames });
    }
    // Animation complete — fire the one-time spawn signal
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonDeckhands: true,
      nextState: {
        ...state,
        node: 'scan',
        phase,
        deckhandsSummoned,
        cooldown: cooldownFor(input.aggressionScale),
        summonFrames: 0,
      },
    };
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * BARGEE_BACKSTEP_SPEED,
        moveY: away.y * BARGEE_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonDeckhands: false,
        nextState: { ...state, phase, deckhandsSummoned, cooldown, backstepFrames },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      deckhandsSummoned,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
    });
  }

  // ── SWING STARTUP (latching to cleat) ─────────────────────────────────────
  if (state.node === 'swing_startup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, deckhandsSummoned, cooldown, windup });
    }
    return noOp(facing, {
      ...state,
      phase,
      deckhandsSummoned,
      cooldown,
      node: 'swinging',
      windup: 0,
      swingFrames: SWING_FRAMES,
    });
  }

  // ── SWINGING (arc — adapter spawns wide hitbox for the full duration) ─────
  if (state.node === 'swinging') {
    if (swingFrames > 0) {
      return {
        moveX: facing * 0.8,
        moveY: 0,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonDeckhands: false,
        nextState: { ...state, phase, deckhandsSummoned, cooldown, swingFrames },
      };
    }
    // Arc complete — fire the sweep hit
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'swing',
      wantsSummonDeckhands: false,
      nextState: {
        ...state,
        phase,
        deckhandsSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        swingFrames: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── HOOK LUNGE WINDUP ─────────────────────────────────────────────────────
  if (state.node === 'hook_lunge_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, deckhandsSummoned, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'hook_lunge',
      wantsSummonDeckhands: false,
      nextState: {
        ...state,
        phase,
        deckhandsSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── HOOK PULL WINDUP ──────────────────────────────────────────────────────
  if (state.node === 'hook_pull_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, deckhandsSummoned, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'hook_pull',
      wantsSummonDeckhands: false,
      nextState: {
        ...state,
        phase,
        deckhandsSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── BARREL WINDUP ─────────────────────────────────────────────────────────
  if (state.node === 'barrel_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, deckhandsSummoned, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'barrel_throw',
      wantsSummonDeckhands: false,
      nextState: {
        ...state,
        phase,
        deckhandsSummoned,
        node: 'scan', // barrel does not backstep; boss stands and reads the result
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const { attackCycle } = state;

  if (cooldown === 0 && input.canAttack) {
    if (attackCycle === 0) {
      // Phase 3: cleat swing instead of lunge
      if (dist <= LUNGE_RANGE) {
        if (phase === 3) {
          return noOp(facing, {
            ...state,
            phase,
            deckhandsSummoned,
            cooldown,
            node: 'swing_startup',
            windup: SWING_STARTUP_FRAMES,
          });
        }
        return noOp(facing, {
          ...state,
          phase,
          deckhandsSummoned,
          cooldown,
          node: 'hook_lunge_windup',
          windup: LUNGE_WINDUP_FRAMES,
        });
      }
    } else if (attackCycle === 1) {
      // Barrel throw fires at any range (Bargee grabs the nearest crate)
      return noOp(facing, {
        ...state,
        phase,
        deckhandsSummoned,
        cooldown,
        node: 'barrel_windup',
        windup: BARREL_WINDUP_FRAMES,
      });
    } else if (attackCycle === 2) {
      if (dist <= PULL_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          deckhandsSummoned,
          cooldown,
          node: 'hook_pull_windup',
          windup: PULL_WINDUP_FRAMES,
        });
      }
    }
  }

  // ── Approach orbit position (same spread logic as EnemyAI) ───────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: BargeeNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonDeckhands: false,
      nextState: {
        ...state,
        phase,
        deckhandsSummoned,
        cooldown,
        node,
        windup: 0,
        swingFrames: 0,
        backstepFrames: 0,
        summonFrames: 0,
      },
    };
  }

  return noOp(facing, { ...state, phase, deckhandsSummoned, cooldown, node: 'scan' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number): number {
  return Math.round(BASE_COOLDOWN * (1 - aggressionScale * 0.5));
}

function toTarget(input: BargeeAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: BargeeAIState): BargeeAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    wantsSummonDeckhands: false,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 3;
}
